import Loading from '../components/Loading';
import { useAuth } from 'react-oidc-context';

import { WishListResponse } from '../interface/interface';
import { formatPrice } from '../utils/numberFormat';
import { TrashIcon } from '../assets/svgImages';
import { useNavigate } from 'react-router-dom';
import { removeWishList } from '../service/removeWishList';
import showToast from '../components/ToastComponent';
import { useContext } from 'react';
import { WishListContext } from '../context/wishListContext';
import { useMutation } from '@tanstack/react-query';
import Breadcrumbs from '../components/Breadcrumbs';

export const PatientWishList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { wishListData, callWishList } = useContext(WishListContext);

  const removeFromWishlistMutation = useMutation({
    mutationKey: ['removeFromWishlist'],
    mutationFn: (wishListId: number) =>
      removeWishList(user?.access_token as string, wishListId),
    onSuccess: (response) => {
      if (typeof response.status === 'number') {
        showToast({
          message: response.message,
          type: 'warning',
        });
        return;
      }
      showToast({
        message: response.message,
        type: 'info',
      });
    },
    onError: (err) => {
      console.log('Error while removing wishlist product:', err);
    },
    onSettled: () => {
      callWishList();
    },
  });

  if (removeFromWishlistMutation.isPending) return <Loading />;

  return (
    <div className="container-standard">
      <Breadcrumbs
        data={[
          {
            tag: 'a',
            href: '/home',
            children: <>Home</>,
          },
          {
            tag: 'p',
            children: <span>My Wishlist</span>,
          },
        ]}
      />
      {wishListData && wishListData.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-secondary-pink-50 px-5 py-6 md:px-8 md:py-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row mb-6 md:items-center md:justify-between">
            <h1 className="text-xl xl:text-3xl text-primary-blue-500 font-semibold">
              My Wishlist
            </h1>
            <span className="text-sm text-gray-500 mt-1 md:mt-0">
              {wishListData.length} item
              {wishListData.length > 1 ? 's' : ''}
            </span>
          </div>

          {/* Table view (desktop / large screens) */}
          <div className="hidden md-large:block overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200 text-left text-sm text-gray-500 uppercase tracking-wide">
                  <th className="py-3 px-4 font-medium text-primary-blue-600">
                    Product
                  </th>
                  <th className="py-3 px-4 font-medium text-primary-blue-600">
                    Name
                  </th>
                  <th className="py-3 px-4 font-medium text-primary-blue-600">
                    Amount
                  </th>
                </tr>
              </thead>

              <tbody>
                {wishListData.map((wishlist: WishListResponse) => {
                  const outOfStock =
                    (wishlist.product?.inventory_quantity ?? 0) < 1;

                  return (
                    <tr
                      key={wishlist.id}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <td className="py-4 px-4">
                        <button
                          type="button"
                          className={`w-24 h-24 flex items-center justify-center rounded-lg border border-gray-100 bg-white overflow-hidden ${
                            outOfStock ? 'grayscale opacity-70' : ''
                          }`}
                          onClick={() =>
                            navigate(
                              `/product/patient/${wishlist?.product?.product_code}`,
                              {
                                state: {
                                  isWishListed: true,
                                },
                              },
                            )
                          }
                        >
                          <img
                            className="max-w-full max-h-full object-contain p-1"
                            src={
                              wishlist.product?.image_url ||
                              '/default-image.png'
                            }
                            alt={wishlist.product?.name}
                          />
                          {outOfStock && (
                            <div className="absolute inset-0 flex items-center justify-center rounded-lg ">
                              <span className="text-white text-xxs font-semibold uppercase tracking-wide bg-primary-blue-500 px-2 py-1 rounded">
                                Out of Stock
                              </span>
                            </div>
                          )}
                        </button>
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={`font-semibold ${
                            outOfStock
                              ? 'text-gray-400'
                              : 'text-primary-blue-500 text-base'
                          }`}
                        >
                          {wishlist.product?.name}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={`font-semibold ${
                            outOfStock
                              ? 'text-gray-400'
                              : 'text-primary-blue-500'
                          }`}
                        >
                          ${formatPrice(wishlist.product?.price)}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            removeFromWishlistMutation.mutate(wishlist.id)
                          }
                          disabled={removeFromWishlistMutation.isPending}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Card view (mobile / smaller screens) */}
          <div className="md-large:hidden space-y-4">
            {wishListData.map((wishlist: WishListResponse) => {
              const outOfStock =
                (wishlist.product?.inventory_quantity ?? 0) < 1;

              return (
                <div
                  key={wishlist.id}
                  className="flex items-center gap-4 bg-gray-50 rounded-xl p-3 border border-gray-100 cursor-pointer"
                >
                  <button
                    type="button"
                    className={`relative w-20 h-20 flex-shrink-0 flex items-center justify-center rounded-lg border border-gray-100 bg-white overflow-hidden ${
                      outOfStock ? 'grayscale opacity-70' : ''
                    }`}
                    onClick={() =>
                      navigate(
                        `/product/patient/${wishlist?.product?.product_code}`,
                        {
                          state: {
                            isWishListed: true,
                          },
                        },
                      )
                    }
                  >
                    <img
                      className={`max-w-full max-h-full object-contain p-1 ${
                        outOfStock ? 'opacity-100' : ''
                      }`}
                      src={wishlist.product?.image_url || '/default-image.png'}
                      alt={wishlist.product?.name}
                    />
                    {outOfStock && (
                      <div className="absolute inset-0 flex items-center justify-center rounded-lg">
                        <span className="text-white text-xxs font-semibold uppercase tracking-wide bg-primary-blue-500 px-1.5 py-0.5 rounded">
                          Out of Stock
                        </span>
                      </div>
                    )}
                  </button>
                  <div className="flex flex-col flex-1 min-w-0">
                    <span
                      className={`text-sm text-primary-blue-500 font-semibold break-words whitespace-normal flex-1  ${
                        outOfStock ? 'text-gray-400' : 'text-gray-800'
                      }`}
                    >
                      {wishlist.product?.name}
                    </span>
                    <span
                      className={`text-sm text-primary-blue-500 font-semibold break-words whitespace-normal flex-1 mt-3 ${
                        outOfStock ? 'text-gray-400' : 'text-primary-blue-500'
                      }`}
                    >
                      ${formatPrice(wishlist.product?.price)}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromWishlistMutation.mutate(wishlist.id);
                    }}
                    className="flex-shrink-0 p-2 text-red-500 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                    aria-label="Remove from wishlist"
                    disabled={removeFromWishlistMutation.isPending}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-secondary-pink-50 px-5 py-12 text-center">
          <p className="text-gray-500 text-lg">Your Wishlist is empty</p>
        </div>
      )}
    </div>
  );
};

export default PatientWishList;
